<template>
  <section class="bg-white py-20 tablet:py-28">
    <div class="container">
      <div class="grid items-center gap-10 tablet:grid-cols-[1.05fr_1fr] tablet:gap-16 [&>*]:min-w-0">
        <!-- Copy -->
        <div class="flex flex-col gap-4">
          <i18n-t
            keypath="landing.amSection.title"
            scope="global"
            tag="h2"
            class="text-[30px] font-extrabold leading-[1.25] tracking-[-0.02em] tablet:text-[42px]"
          >
            <template #br><br /></template>
            <template #accent
              ><span class="text-gold-primary-120">{{ t('landing.amSection.titleAccent') }}</span></template
            >
          </i18n-t>
          <p class="mt-4 text-[17px] leading-[1.6] text-black-80 tablet:text-lg">
            {{ t('landing.amSection.p1') }}
          </p>
          <p class="text-[17px] leading-[1.6] text-black-80 tablet:text-lg">
            {{ t('landing.amSection.p2') }}
          </p>
          <p class="text-[17px] font-bold leading-[1.6] text-black-100 tablet:text-lg">
            {{ t('landing.amSection.p3') }}
          </p>
        </div>

        <!-- AM Card -->
        <div>
          <div
            class="mx-auto w-full max-w-[460px] rounded-[24px] border border-gold-primary-100 bg-gradient-to-br from-gold-primary-10 to-white to-60% p-6 shadow-[0_20px_50px_rgba(0,0,0,0.08)] tablet:p-7"
          >
            <div class="flex items-center gap-4">
              <template v-if="isUae">
                <img
                  v-if="am.photo"
                  :src="am.photo"
                  :alt="am.name"
                  class="size-[72px] shrink-0 rounded-full object-cover shadow-[0_4px_12px_rgba(0,0,0,0.08)]"
                />
                <!-- Neutral placeholder until [AM_PHOTO_1] is supplied -->
                <div
                  v-else
                  class="flex size-[72px] shrink-0 items-center justify-center rounded-full bg-gold-primary-20 text-xl font-extrabold text-gold-primary-120 shadow-[0_4px_12px_rgba(0,0,0,0.08)] ring-1 ring-gold-primary-30"
                  aria-hidden="true"
                >
                  {{ am.initials }}
                </div>
                <div class="min-w-0">
                  <h3 class="text-[19px] font-extrabold leading-tight tracking-[-0.01em] text-black-100">
                    {{ am.name }}
                  </h3>
                  <p class="mt-1 text-[13px] leading-tight text-black-60">{{ t('landing.uae.am.role') }}</p>
                </div>
              </template>
              <template v-else>
                <div
                  class="flex size-[72px] shrink-0 items-center justify-center rounded-full bg-black-100 text-gold-primary-100 shadow-[0_4px_12px_rgba(0,0,0,0.08)]"
                  aria-hidden="true"
                >
                  <svg
                    viewBox="0 0 24 24"
                    class="size-8"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="1.8"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  >
                    <circle
                      cx="12"
                      cy="8"
                      r="4"
                    />
                    <path d="M4 21v-1a6 6 0 0 1 6-6h4a6 6 0 0 1 6 6v1" />
                  </svg>
                </div>
                <h3 class="min-w-0 text-[19px] font-extrabold leading-tight tracking-[-0.01em] text-black-100">
                  {{ t('landing.amSection.cardTitle') }}
                </h3>
              </template>
            </div>
            <div
              class="mt-[18px] flex flex-col gap-2.5 border-y border-gold-primary-30 py-3.5 text-[13px] leading-[1.3] text-black-80"
            >
              <div
                v-if="isUae"
                class="flex items-center gap-2.5 border-b border-gold-primary-30 pb-2.5"
              >
                <svg
                  viewBox="0 0 24 24"
                  class="size-[15px] shrink-0 text-gold-primary-120"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  aria-hidden="true"
                >
                  <circle
                    cx="12"
                    cy="12"
                    r="9"
                  />
                  <path d="M3 12h18M12 3a14 14 0 0 1 0 18M12 3a14 14 0 0 0 0 18" />
                </svg>
                <b class="font-bold text-black-100">{{ t('landing.uae.am.speaksLabel') }}</b>
                <ul class="flex min-w-0 flex-wrap gap-1.5">
                  <li
                    v-for="language in amLanguages"
                    :key="language"
                    class="rounded-full bg-gold-primary-20 px-2 py-0.5 text-[11px] font-semibold text-black-100"
                  >
                    {{ language }}
                  </li>
                </ul>
              </div>
              <div class="flex items-center gap-2.5">
                <svg
                  viewBox="0 0 24 24"
                  class="size-[15px] shrink-0 text-gold-primary-120"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                >
                  <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
                  <circle
                    cx="12"
                    cy="10"
                    r="3"
                  />
                </svg>
                <span
                  ><b class="me-1 font-bold text-black-100">{{ t('landing.amSection.coversLabel') }}</b
                  >{{ t('landing.amSection.coversValue') }}</span
                >
              </div>
            </div>

            <NuxtLink
              :to="applyHref"
              @click="trackCtaClick('am')"
              class="mt-[18px] inline-flex h-12 w-full items-center justify-center gap-2 rounded-full bg-black-100 px-5 text-sm font-bold text-white transition-transform hover:-translate-y-px"
            >
              {{ t('landing.header.getAccess') }}
            </NuxtLink>

            <div
              v-if="isUae"
              class="mt-5 flex items-center gap-3 rounded-[14px] border border-dashed border-gold-primary-30 bg-white px-4 py-3.5"
            >
              <div
                class="flex shrink-0 -space-x-2"
                aria-hidden="true"
              >
                <template
                  v-for="(photo, index) in am.teamPhotos"
                  :key="index"
                >
                  <img
                    v-if="photo"
                    :src="photo"
                    alt=""
                    class="size-7 rounded-full object-cover ring-2 ring-white"
                  />
                  <!-- Silhouette until [AM_PHOTO_2..4] are supplied -->
                  <span
                    v-else
                    class="flex size-7 items-center justify-center rounded-full bg-black-10 text-black-40 ring-2 ring-white"
                  >
                    <svg
                      viewBox="0 0 24 24"
                      class="size-4"
                      fill="currentColor"
                    >
                      <circle
                        cx="12"
                        cy="9"
                        r="4"
                      />
                      <path d="M4 21a8 8 0 0 1 16 0z" />
                    </svg>
                  </span>
                </template>
              </div>
              <p class="min-w-0 text-xs leading-[1.35] text-black-60">{{ t('landing.uae.am.teamNote') }}</p>
            </div>
            <p
              v-else
              class="mt-5 rounded-[14px] border border-dashed border-gold-primary-30 bg-white px-4 py-3.5 text-xs leading-[1.35] text-black-60"
            >
              {{ t('landing.amSection.floor') }}
            </p>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<script setup lang="ts">
import type { SiteMarket } from '~/constants/market';
import { UAE_AM_CARD } from '~/constants/uaeAmCard';

// Only /uae passes market="uae"; the International card stays as it was.
const { market } = defineProps<{ market?: SiteMarket }>();

const { t, locale } = useI18n();

const amLanguages = computed(() => {
  const lang = locale?.value || 'en';
  const names = new Intl.DisplayNames([lang, 'en'], { type: 'language' });
  return am.languages.map((code) => {
    const name = names.of(code) ?? code;
    return name.charAt(0).toLocaleUpperCase(lang) + name.slice(1);
  });
});
const isUae = computed(() => market === 'uae');
const am = UAE_AM_CARD;

const { signupHref: applyHref } = useSignupHref();
const { trackCtaClick } = useLandingTracking();
</script>
